import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Column } from 'react-table';
import { FileSpreadsheet, RotateCcw } from 'lucide-react';
import DefaultLayout from '../../../layout/DefaultLayout';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import DataTable from '../../../components/DataTable/DataTable';
import ActionButton from '../../../components/Buttons/ActionButton';
import NegativeStockFilters from '../../../components/Report/NegativeStock/NegativeStockFilters';
import NegativeStockReportService, {
  NegativeStockReportFilters as FiltersType,
} from '../../../services/inventory/NegativeStockReportService';
import { useBranch } from '../../../hooks/branches/useBranch';
import { useProductCategories } from '../../../hooks/useProductCategories';
import ProductReportGroupService from '../../../services/report/ProductReportGroupService';
import ReportExportService from '../../../services/report/ReportExportService';
import { REPORT_CODES } from '../../../features/reports/reportCodes';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import useAlerts from '../../../hooks/useAlerts';
import useConfirmModal from '../../../hooks/useConfirmModal';
import InventoryAdjustmentService from '../../../services/inventory/adjustment/InventoryAdjustmentService';

interface NegativeStockRow {
  productId: number;
  productCode: string;
  productName: string;
  categoryName: string;
  branchName: string;
  stockQuantity: number;
  minStock: number;
}

const NegativeStockReportPage: React.FC = () => {
  const { page } = useParams();
  const currentPage = page ? parseInt(page, 10) : 0;
  const pageSize = 10;

  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { fetchProductCategoriesDropdown, productCategoriesDropdown } =
    useProductCategories();

  const { handle } = useApiErrorHandler();
  const { show: showAlert } = useAlerts();
  const confirm = useConfirmModal();
  const navigate = useNavigate();

  const [rows, setRows] = useState<NegativeStockRow[]>([]);
  const [paginator, setPaginator] = useState<any>(null);
  const [filters, setFilters] = useState<FiltersType>({});
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const fetchReport = async (
    appliedFilters: FiltersType,
    pageIndex: number
  ) => {
    const response = await NegativeStockReportService.search(
      appliedFilters,
      pageIndex,
      pageSize
    );
    setRows(response?.content ?? []);
    setPaginator(response);
  };

  useEffect(() => {
    fetchBranchDropdown();
    fetchProductCategoriesDropdown();
    ProductReportGroupService.getActive().then((data) => setGroups(data ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchReport(filters, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleSearch = (newFilters: FiltersType) => {
    setFilters(newFilters);
    fetchReport(newFilters, 0);
  };

  const handleReset = () => {
    setFilters({});
    fetchReport({}, 0);
  };

  const handleResetGroup = () => {
    if (!filters.groupId || !filters.branchId) return;

    const groupName = groups.find((g) => g.id === filters.groupId)?.name ?? 'este grupo';
    const branchName = branchDropdown.find((b: any) => b.id === filters.branchId)?.name ?? 'esta sucursal';

    confirm({
      title: `¿Reiniciar a 0 "${groupName}"?`,
      message: `Se creará un ajuste de inventario EN BORRADOR con los productos de "${groupName}" que estén en negativo en "${branchName}", cada uno llevado a 0. No se aplica nada todavía — deberás revisarlo y aprobarlo en Ajuste de Inventario.`,
      icon: 'warning',
      confirmText: 'Crear borrador',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        setIsResetting(true);
        try {
          const response = await InventoryAdjustmentService.resetGroupToZero({
            groupId: filters.groupId!,
            branchId: filters.branchId!,
          });
          showAlert(response.type, response.title, response.message);
          navigate('/inventory/adjustments');
        } catch (error: any) {
          handle(error);
        } finally {
          setIsResetting(false);
        }
      },
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await ReportExportService.exportReport({
        reportCode: REPORT_CODES.STOCK_NEGATIVO,
        filters: {
          branchId: filters.branchId ?? null,
          categoryId: filters.categoryId ?? null,
          groupId: filters.groupId ?? null,
        },
      });
    } catch (error: any) {
      handle(error);
    } finally {
      setIsExporting(false);
    }
  };

  const columns: Column<NegativeStockRow>[] = [
    { Header: 'Código', accessor: 'productCode' },
    { Header: 'Producto', accessor: 'productName' },
    { Header: 'Categoría', accessor: 'categoryName' },
    { Header: 'Sucursal', accessor: 'branchName' },
    {
      Header: 'Stock Actual',
      accessor: 'stockQuantity',
      Cell: ({ value }) => (
        <span className="font-semibold text-danger">{value}</span>
      ),
    },
    { Header: 'Stock Mínimo', accessor: 'minStock' },
  ];

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <div className="mb-5 rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
        <NegativeStockFilters
          branches={branchDropdown}
          categories={productCategoriesDropdown}
          groups={groups}
          onSearch={handleSearch}
          onReset={handleReset}
        />
      </div>

      <div className="mb-3 flex flex-wrap justify-end gap-2">
        {filters.groupId && filters.branchId ? (
          <ActionButton
            label="Reiniciar a 0 este grupo"
            icon={<RotateCcw className="h-4 w-4" />}
            variant="danger"
            size="sm"
            fullWidth={false}
            loading={isResetting}
            loadingLabel="Creando borrador..."
            disabled={rows.length === 0}
            onClick={handleResetGroup}
          />
        ) : null}
        <ActionButton
          label="Exportar a Excel"
          icon={<FileSpreadsheet className="h-4 w-4" />}
          variant="success"
          size="sm"
          fullWidth={false}
          loading={isExporting}
          loadingLabel="Exportando..."
          disabled={rows.length === 0}
          onClick={handleExport}
        />
      </div>

      <DataTable
        columns={columns}
        data={rows}
        pageSizeOptions={[10, 20, 50]}
        url="/reports/negative-stock"
        paginator={paginator}
        onPageSizeChange={() => undefined}
        noDataMessage="No hay productos con stock negativo para estos filtros."
      />
    </DefaultLayout>
  );
};

export default NegativeStockReportPage;
